import {
  User,
  Briefcase,
  MapPin,
  Brain,
  Sparkles,
  MessageCircle,
  Users,
  Globe,
  Calendar,
} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {Badge} from "@/components/ui/badge";
import {getPsychotypeColor, type AiPersona} from "@/lib/personas-api";

interface PersonaDetailsTabProps {
  profile: AiPersona;
  className?: string;
}

export function PersonaDetailsTab({profile, className}: PersonaDetailsTabProps) {
  const psychotypeColorClass = getPsychotypeColor(profile.psychotype);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Demographics */}
      <DetailsSection
        icon={User}
        title={m.persona_details_demographics()}
        iconClassName="text-blue-400"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <DetailItem label={m.persona_details_age_range()} value={profile.age_range} />
          <DetailItem label={m.persona_details_gender()} value={profile.gender} />
          <DetailItem
            label={m.persona_card_occupation()}
            value={profile.occupation}
            icon={Briefcase}
          />
          <DetailItem
            label={m.persona_details_location_context()}
            value={profile.location_context}
            icon={MapPin}
          />
        </div>
      </DetailsSection>

      {/* Personality */}
      <DetailsSection
        icon={Brain}
        title={m.persona_details_personality()}
        iconClassName="text-purple-400"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground text-sm">{m.persona_details_psychotype()}:</span>
            <Badge
              variant="outline"
              className={cn("px-3 py-1 text-sm font-semibold", psychotypeColorClass)}
            >
              {profile.psychotype}
            </Badge>
          </div>

          <BadgeList
            label={m.persona_details_traits()}
            items={profile.personality_traits}
            badgeIcon={Sparkles}
          />

          <InfoCard icon={MessageCircle} title={m.persona_details_communication_style()}>
            {profile.communication_style}
          </InfoCard>
        </div>
      </DetailsSection>

      {/* Behavior */}
      <DetailsSection
        icon={Users}
        title={m.persona_details_behavior()}
        iconClassName="text-green-400"
      >
        <p className="text-muted-foreground text-sm leading-relaxed">{profile.social_behavior}</p>
      </DetailsSection>

      {/* Preferences */}
      <DetailsSection
        icon={Calendar}
        title={m.persona_details_preferences()}
        iconClassName="text-orange-400"
      >
        <div className="space-y-4">
          <BadgeList
            label={m.persona_details_platform_preferences()}
            labelIcon={Globe}
            items={profile.platform_preferences}
            variant="outline"
            badgeClassName="border-border/50 bg-background/50"
          />

          <InfoCard icon={Calendar} title={m.persona_details_event_preferences()}>
            {profile.event_preferences}
          </InfoCard>

          <BadgeList label={m.persona_card_interests()} items={profile.interests} />
        </div>
      </DetailsSection>
    </div>
  );
}

// Reusable section wrapper
interface DetailsSectionProps {
  icon: React.ComponentType<{className?: string}>;
  title: string;
  iconClassName?: string;
  children: React.ReactNode;
}

function DetailsSection({icon: Icon, title, iconClassName, children}: DetailsSectionProps) {
  return (
    <div className="border-border/40 bg-card/30 rounded-xl border p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <div className="bg-muted/50 flex h-8 w-8 items-center justify-center rounded-lg">
          <Icon className={cn("h-4 w-4", iconClassName)} />
        </div>
        <h3 className="font-semibold tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Reusable label/value pair
interface DetailItemProps {
  label: string;
  value: string;
  icon?: React.ComponentType<{className?: string}>;
}

function DetailItem({label, value, icon: Icon}: DetailItemProps) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground/70 flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

// Reusable info card with icon and description
interface InfoCardProps {
  icon: React.ComponentType<{className?: string}>;
  title: string;
  children: React.ReactNode;
}

function InfoCard({icon: Icon, title, children}: InfoCardProps) {
  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <p className="mb-1.5 flex items-center gap-2 text-sm font-medium">
        <Icon className="text-muted-foreground h-4 w-4" />
        {title}
      </p>
      <p className="text-muted-foreground text-sm leading-relaxed">{children}</p>
    </div>
  );
}

// Reusable badge list with optional label
interface BadgeListProps {
  items: string[];
  label?: string;
  labelIcon?: React.ComponentType<{className?: string}>;
  badgeIcon?: React.ComponentType<{className?: string}>;
  variant?: "secondary" | "outline";
  badgeClassName?: string;
}

function BadgeList({
  items,
  label,
  labelIcon: LabelIcon,
  badgeIcon: BadgeIcon,
  variant = "secondary",
  badgeClassName,
}: BadgeListProps) {
  return (
    <div>
      {label && (
        <p className="text-muted-foreground mb-2.5 flex items-center gap-2 text-sm">
          {LabelIcon && <LabelIcon className="h-4 w-4" />}
          {label}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <Badge
            key={item}
            variant={variant}
            className={cn("bg-muted/50 font-normal", badgeClassName)}
          >
            {BadgeIcon && <BadgeIcon className="mr-1.5 h-3 w-3 text-amber-400/70" />}
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}
